import {
  DEMO_MIN_STAKE,
  ROUND_ACTIVE_MS,
  quoteDemoTrade,
  quoteLiveTrade,
  type GameMode,
  type PredictionDirection,
  type RoundStatus,
  type RoundView,
} from "@priceon/shared";

export type Direction = PredictionDirection;
export type LiveTradeStatus = "winning" | "losing" | "neutral";
export type ChartTone = "up" | "down" | "neutral";
export type PriceConnectionState =
  | "connecting"
  | "online"
  | "reconnecting"
  | "server-unavailable"
  | "device-offline";

export interface PricePoint {
  timestamp: number;
  price: number;
}

export interface DisplayRound {
  id: string;
  status: RoundStatus;
  startsAt: number;
  endsAt: number;
  startPrice: number;
  closePrice: number | null;
}

export interface PriceDomain {
  min: number;
  max: number;
}

export interface ChartPriceDomainKey {
  roundId?: string;
  startPrice: number;
  lineChart: boolean;
}

export interface StakeAvailability {
  mode: GameMode;
  demoAvailableBalance?: string;
  liveEnabled?: boolean;
  liveAvailableLamports?: string;
  liveMinimumStakeLamports?: string;
  liveMaximumStakeLamports?: string;
}

export interface Candlestick {
  timestamp: number;  // Mum başlama zamanı
  open: number;
  high: number;
  low: number;
  close: number;
  isLive?: boolean;   // Son mum (henüz kapalı değil)
}
export type RoundPhase = "live" | "pick-open" | "closing";

const STAKE_PRESET_MULTIPLIERS = [1n, 2n, 5n] as const;

export function getDynamicStakePresets(
  availableBalanceSol: string | null | undefined,
  maximumStakeSol?: string | null,
): string[] {
  const balanceTenths = parseWholeTenths(availableBalanceSol);
  const balanceTier = balanceTenths !== undefined && balanceTenths > 0n ? balanceTenths : 1n;
  const maximumHundredths = parseWholeHundredths(maximumStakeSol);
  const maximumTier = maximumHundredths === undefined
    ? balanceTier
    : maximumHundredths / 5n > 0n ? maximumHundredths / 5n : 1n;
  const tier = maximumTier < balanceTier ? maximumTier : balanceTier;
  return STAKE_PRESET_MULTIPLIERS.map((multiplier) => formatHundredths(tier * multiplier));
}

export function isStakeAllowed(stakeSol: string, availability: StakeAvailability): boolean {
  if (availability.mode === "live") {
    const quote = quoteLiveTrade(stakeSol);
    const available = parseUnsignedInteger(availability.liveAvailableLamports);
    const minimum = parseUnsignedInteger(availability.liveMinimumStakeLamports);
    const maximum = parseUnsignedInteger(availability.liveMaximumStakeLamports);
    if (!availability.liveEnabled || !quote || available === undefined || minimum === undefined || maximum === undefined) {
      return false;
    }
    const stake = BigInt(quote.stakeLamports);
    return stake >= minimum && stake <= maximum && BigInt(quote.totalCostLamports) <= available;
  }

  const quote = quoteDemoTrade(stakeSol);
  const stake = quote ? parseFixedDecimal(quote.stakeSol, 4) : undefined;
  const totalCost = quote ? parseFixedDecimal(quote.totalCostSol, 4) : undefined;
  const available = parseFixedDecimal(availability.demoAvailableBalance, 4);
  const minimum = parseFixedDecimal(DEMO_MIN_STAKE, 4);
  return stake !== undefined && totalCost !== undefined && available !== undefined && minimum !== undefined
    && stake >= minimum && totalCost <= available;
}

export function resolveStakeSelection(
  currentStake: string,
  presets: readonly string[],
  isAllowed: (stakeSol: string) => boolean,
): string {
  if (isAllowed(currentStake)) return currentStake;
  return presets.find(isAllowed) ?? presets[0] ?? currentStake;
}

function parseWholeTenths(value: string | null | undefined): bigint | undefined {
  if (!value) return undefined;
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return undefined;
  return BigInt(match[1]!) * 10n + BigInt(match[2]?.[0] ?? "0");
}

function parseWholeHundredths(value: string | null | undefined): bigint | undefined {
  if (!value) return undefined;
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return undefined;
  return BigInt(match[1]!) * 100n + BigInt((match[2] ?? "").padEnd(2, "0").slice(0, 2));
}

function formatHundredths(value: bigint): string {
  return `${value / 100n}.${String(value % 100n).padStart(2, "0")}`;
}

function parseUnsignedInteger(value: string | null | undefined): bigint | undefined {
  return value && /^\d+$/.test(value) ? BigInt(value) : undefined;
}

function parseFixedDecimal(value: string | null | undefined, decimals: number): bigint | undefined {
  if (!value) return undefined;
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match || (match[2]?.length ?? 0) > decimals) return undefined;
  return BigInt(match[1]!) * (10n ** BigInt(decimals))
    + BigInt((match[2] ?? "").padEnd(decimals, "0"));
}

export function areBattlesEnabled(modes?: { battles?: { enabled?: boolean } } | null): boolean {
  return modes?.battles?.enabled === true;
}

export function getResultOverlayStorageKey(userId: string, mode: GameMode): string {
  return `priceon:result-overlay:last-shown:${userId}:${mode}`;
}

export function shouldShowResultOverlay(resultKey: string, lastShownResultKey?: string | null): boolean {
  return resultKey.length > 0 && resultKey !== lastShownResultKey;
}

export function resolveGameModeAfterModesLoad(
  currentMode: GameMode,
  initialized: boolean,
  modes: { defaultMode: GameMode; live: { enabled: boolean; invited: boolean } },
): GameMode {
  if (!modes.live.invited) return "demo";
  if (!modes.live.enabled) return currentMode;
  if (initialized) return currentMode;
  return modes.defaultMode === "live" ? "live" : "demo";
}

export function toDisplayRound(round: RoundView | null | undefined): DisplayRound | undefined {
  if (!round || (round.status !== "open" && round.status !== "closed") || round.startPrice === null) return undefined;
  const startsAt = Date.parse(round.startsAt);
  const endsAt = Date.parse(round.endsAt);
  const startPrice = Number(round.startPrice);
  const closePrice = round.closePrice === null ? null : Number(round.closePrice);
  if (!Number.isFinite(startsAt) || !Number.isFinite(endsAt) || !Number.isFinite(startPrice) || (closePrice !== null && !Number.isFinite(closePrice))) {
    return undefined;
  }
  return { id: round.id, status: round.status, startsAt, endsAt, startPrice, closePrice };
}

export function resolvePlayChartRound(
  round: DisplayRound | undefined,
  previousRound: DisplayRound | undefined,
): DisplayRound | undefined {
  return round ?? previousRound;
}

export function toScheduledDisplayRound(
  upcoming: RoundView | null | undefined,
  price: number | undefined,
): DisplayRound | undefined {
  if (!upcoming || price === undefined) return undefined;
  const startsAt = Date.parse(upcoming.startsAt);
  const endsAt = Date.parse(upcoming.endsAt);
  if (!Number.isFinite(startsAt) || !Number.isFinite(endsAt) || !Number.isFinite(price)) return undefined;
  return {
    id: upcoming.id,
    status: "open",
    startsAt,
    endsAt,
    startPrice: price,
    closePrice: null,
  };
}

export function appendLivePricePoint(
  points: PricePoint[],
  point: PricePoint,
  round: DisplayRound,
): PricePoint[] {
  if (point.timestamp < round.startsAt || point.timestamp >= round.endsAt) return points;
  const bucket = Math.floor(point.timestamp / 250);
  const last = points.at(-1);
  const next = last && Math.floor(last.timestamp / 250) === bucket
    ? [...points.slice(0, -1), point]
    : [...points, point];
  return next;
}

export function generateCandlesticks(
  points: PricePoint[],
  currentPrice: number,
  now: number,
  round?: DisplayRound,
  lookbackMinutes: number = 9,
): Candlestick[] {
  const candleSeconds = 60 * 1000; // 1 minute per candlestick
  const numCandles = lookbackMinutes;

  // Always look back N minutes from now, regardless of round
  // This allows users to see historical context for technical analysis
  const startTime = Math.floor(now / candleSeconds) * candleSeconds - (numCandles - 1) * candleSeconds;
  
  const sortedPoints = [...points].sort((a, b) => a.timestamp - b.timestamp);
  const candles: Candlestick[] = [];

  for (let i = 0; i < numCandles; i++) {
    const candleStart = startTime + (i * candleSeconds);
    const candleEnd = candleStart + candleSeconds;
    const isLive = i === numCandles - 1;

    // Collect points for this candlestick
    const candlePoints = sortedPoints.filter(
      (p) => p.timestamp >= candleStart && p.timestamp < candleEnd
    );

    if (candlePoints.length === 0) {
      // Use the last known close or current price as placeholder
      const placeholderPrice = i > 0 && candles[i - 1]
        ? candles[i - 1].close
        : currentPrice;
      candles.push({
        timestamp: candleStart,
        open: placeholderPrice,
        high: placeholderPrice,
        low: placeholderPrice,
        close: placeholderPrice,
        isLive,
      });
      continue;
    }

    const open = candlePoints[0].price;
    let close = candlePoints[candlePoints.length - 1].price;
    let high = Math.max(...candlePoints.map((p) => p.price));
    let low = Math.min(...candlePoints.map((p) => p.price));

    // For live candle, use current price to show real-time movement
    if (isLive) {
      close = currentPrice;
      high = Math.max(high, currentPrice);
      low = Math.min(low, currentPrice);
    }

    candles.push({
      timestamp: candleStart,
      open,
      high,
      low,
      close,
      isLive,
    });
  }

  return candles;
}

export function getSecondsRemaining(now: number, round: DisplayRound): number {
  return Math.max(0, Math.ceil((round.endsAt - now) / 1000));
}

export function getRoundProgressPercent(now: number, round: DisplayRound): number {
  const duration = round.endsAt - round.startsAt;
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return Math.max(0, Math.min(100, ((now - round.startsAt) / duration) * 100));
}

export function getRoundTimePercent(timestamp: number, round: DisplayRound): number {
  const duration = round.endsAt - round.startsAt;
  if (!Number.isFinite(timestamp) || !Number.isFinite(duration) || duration <= 0) return 0;
  return Math.max(0, Math.min(100, ((timestamp - round.startsAt) / duration) * 100));
}

export function getRoundPhase(
  now: number,
  round: DisplayRound | undefined,
  upcoming: RoundView | null | undefined,
): RoundPhase {
  if (isPredictionWindowOpen(now, upcoming)) return "pick-open";
  if (!round) return "live";
  return getSecondsRemaining(now, round) <= 2 ? "closing" : "live";
}

export function isDecisionPhase(
  now: number,
  round: DisplayRound | undefined,
  upcoming: RoundView | null | undefined,
): boolean {
  if (!isPredictionWindowOpen(now, upcoming)) return false;
  const decisionRound = !round || round.endsAt <= now;
  const legacyOverlap = Boolean(round && round.endsAt - round.startsAt > ROUND_ACTIVE_MS);
  return decisionRound || legacyOverlap;
}

export function getExpandedPriceDomain(
  points: PricePoint[],
  startPrice: number,
  previous?: PriceDomain,
): PriceDomain {
  const prices = [startPrice, ...points.map((point) => point.price)]
    .filter((price) => Number.isFinite(price));
  const low = Math.min(...prices);
  const high = Math.max(...prices);
  const minimumHalfRange = Math.max(0.12, Math.abs(startPrice) * 0.001);
  const halfRange = Math.max(
    minimumHalfRange,
    Math.abs(low - startPrice),
    Math.abs(high - startPrice),
  );
  const padding = halfRange * 0.2;
  const next = {
    min: Math.min(low, startPrice - halfRange) - padding,
    max: Math.max(high, startPrice + halfRange) + padding,
  };

  if (!previous || !Number.isFinite(previous.min) || !Number.isFinite(previous.max)) return next;
  return {
    min: Math.min(previous.min, next.min),
    max: Math.max(previous.max, next.max),
  };
}

export function canReuseChartPriceDomain(
  previous: (ChartPriceDomainKey & PriceDomain) | undefined,
  next: ChartPriceDomainKey,
): boolean {
  return Boolean(
    previous?.roundId
    && previous.roundId === next.roundId
    && previous.startPrice === next.startPrice
    && previous.lineChart === next.lineChart,
  );
}

export function formatPriceDelta(change: number): string {
  if (!Number.isFinite(change)) return "--";
  const absolute = Math.abs(change);
  const decimals = absolute > 0 && absolute < 0.01 ? 4 : 2;
  return `${change >= 0 ? "+" : "-"}${absolute.toFixed(decimals)}`;
}

export function isPredictionWindowOpen(now: number, round: RoundView | null | undefined): boolean {
  if (!round || round.status !== "scheduled") return false;
  const opensAt = Date.parse(round.predictionOpensAt);
  const closesAt = Date.parse(round.predictionClosesAt);
  return Number.isFinite(opensAt) && Number.isFinite(closesAt) && now >= opensAt && now < closesAt;
}

export function getServerOffset(serverTime: string, receivedAt: number): number {
  const parsed = Date.parse(serverTime);
  return Number.isFinite(parsed) ? parsed - receivedAt : 0;
}

export function getNextRoundRefreshDelay(
  now: number,
  current: RoundView | null | undefined,
  upcoming: RoundView | null | undefined,
): number {
  if (upcoming) {
    const opensAt = Date.parse(upcoming.predictionOpensAt);
    const startsAt = Date.parse(upcoming.startsAt);
    const boundary = now < opensAt ? opensAt : startsAt;
    return Math.max(250, Math.min(30_000, boundary - now + 150));
  }
  if (current) {
    const opensAt = Date.parse(current.endsAt);
    const endsAt = Date.parse(current.endsAt);
    if (now >= opensAt && now < endsAt) return 500;
    if (now < opensAt) return Math.max(250, Math.min(30_000, opensAt - now + 150));
    return 500;
  }
  return 1_000;
}

export function getLiveTradeStatus(
  direction: Direction,
  startPrice: number,
  currentPrice: number,
): LiveTradeStatus {
  if (currentPrice === startPrice) return "neutral";
  return direction === "up"
    ? currentPrice > startPrice ? "winning" : "losing"
    : currentPrice < startPrice ? "winning" : "losing";
}

export function getChartTone(
  startPrice: number,
  currentPrice: number,
  direction?: Direction,
): ChartTone {
  if (direction) {
    const status = getLiveTradeStatus(direction, startPrice, currentPrice);
    return status === "winning" ? "up" : status === "losing" ? "down" : "neutral";
  }
  return currentPrice > startPrice ? "up" : currentPrice < startPrice ? "down" : "neutral";
}

export function isPriceAvailable(connection: PriceConnectionState): boolean {
  return connection === "online";
}

export type PriceSeriesStatus = "loading" | "ready" | "error";
export type MiniAppAuthState = "checking" | "authenticated" | "registration-required" | "error";

export const MINI_APP_VIEWPORT_STABLE_TIMEOUT_MS = 400;
export const MINI_APP_PLAY_BOOT_TIMEOUT_MS = 8_000;

export function canPaintPriceChart(args: {
  price?: number;
  points: PricePoint[];
  round?: DisplayRound;
  now: number;
  lineChart?: boolean;
}): boolean {
  const { price, points, round, now, lineChart = false } = args;
  if (price === undefined || !Number.isFinite(price)) return false;
  if (lineChart) return true;
  if (points.length === 0) return false;
  const candles = round
    ? generateCandlesticks(
      [{ timestamp: round.startsAt, price: round.startPrice }, ...points],
      price,
      now,
      round,
    )
    : generateCandlesticks(points, price, now, undefined, 10);
  return candles.length > 0;
}

export function shouldRevealMiniAppPlay(args: {
  authState: MiniAppAuthState;
  alreadyRevealed?: boolean;
  viewportStable: boolean;
  roundStateSettled: boolean;
  chartReady: boolean;
  bootTimedOut: boolean;
}): boolean {
  if (args.authState !== "authenticated") return false;
  if (args.alreadyRevealed) return true;
  if (args.bootTimedOut) return true;
  return args.viewportStable && args.roundStateSettled && args.chartReady;
}

export type PaintedPriceChart = {
  points: PricePoint[];
  price: number;
  round?: DisplayRound;
};

export function holdPaintedPriceChart(args: {
  price?: number;
  points: PricePoint[];
  round?: DisplayRound;
  now: number;
  lineChart?: boolean;
  lastPaint?: PaintedPriceChart;
}): { paint?: PaintedPriceChart; showLoading: boolean } {
  if (canPaintPriceChart(args) && args.price !== undefined) {
    return {
      paint: { points: args.points, price: args.price, round: args.round },
      showLoading: false,
    };
  }
  if (args.lastPaint) {
    return { paint: args.lastPaint, showLoading: false };
  }
  return { showLoading: true };
}

export function resolveMiniAppBootViewportHeight(args: {
  innerHeight: number;
  screenHeight: number;
  coarsePointer?: boolean;
  isTelegram?: boolean;
  isExpanded?: boolean;
  viewportHeight?: number;
  viewportStableHeight?: number;
}): number {
  const innerHeight = Math.max(0, args.innerHeight || 0);
  const screenHeight = Math.max(0, args.screenHeight || 0);
  const telegramHeight = Math.max(args.viewportHeight || 0, args.viewportStableHeight || 0);

  if (args.isTelegram && args.isExpanded && telegramHeight > 0) return telegramHeight;

  const useScreenHeight = Boolean(args.coarsePointer || args.isTelegram) && screenHeight > innerHeight;
  return Math.max(innerHeight, useScreenHeight ? screenHeight : 0, telegramHeight);
}

export function lockMiniAppBootViewportHeight(currentLockPx: number, nextHeightPx: number): number {
  const current = Number.isFinite(currentLockPx) && currentLockPx > 0 ? currentLockPx : 0;
  const next = Number.isFinite(nextHeightPx) && nextHeightPx > 0 ? nextHeightPx : 0;
  return Math.max(current, next);
}
